import { useCallback, useMemo, useState } from 'react';

/**
 * Set-based row selection that survives pagination, plus a cache of the selected
 * row objects so exports can include rows from pages no longer loaded.
 */
export function useBulkSelection(rows = []) {
  const [selectedIds, setSelectedIds] = useState(() => new Set());
  const [cache, setCache] = useState(() => new Map());

  const remember = useCallback((items) => {
    setCache((current) => {
      const next = new Map(current);
      (items || []).forEach((item) => item?.id && next.set(item.id, item));
      return next;
    });
  }, []);

  const toggleOne = useCallback((row) => {
    const id = typeof row === 'string' ? row : row?.id;
    if (!id) return;
    if (typeof row === 'object') remember([row]);
    setSelectedIds((current) => {
      const next = new Set(current);
      if (next.has(id)) next.delete(id); else next.add(id);
      return next;
    });
  }, [remember]);

  const togglePage = useCallback(() => {
    remember(rows);
    setSelectedIds((current) => {
      const next = new Set(current);
      const everyOn = rows.length > 0 && rows.every((row) => next.has(row.id));
      rows.forEach((row) => (everyOn ? next.delete(row.id) : next.add(row.id)));
      return next;
    });
  }, [rows, remember]);

  const clear = useCallback(() => setSelectedIds(new Set()), []);

  const allOnPageSelected = useMemo(
    () => rows.length > 0 && rows.every((row) => selectedIds.has(row.id)),
    [rows, selectedIds]
  );
  const selectedOnPageCount = useMemo(
    () => rows.filter((row) => selectedIds.has(row.id)).length,
    [rows, selectedIds]
  );
  const selectedRows = useMemo(
    () => Array.from(selectedIds).map((id) => cache.get(id)).filter(Boolean),
    [selectedIds, cache]
  );

  return {
    selectedIds,
    selectedRows,
    selectedCount: selectedIds.size,
    selectedOnPageCount,
    allOnPageSelected,
    isSelected: (id) => selectedIds.has(id),
    toggleOne,
    togglePage,
    clear,
    remember,
  };
}

export default useBulkSelection;
